import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, RadioChangeEvent, Row, Select, Space } from "antd";

import AddressMaker, { AddressType } from "./AddressMaker";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import * as String from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";
import BBSEditor from "./BBSEditor";

const formItemLayout = {
    labelCol: {
        sm: {
            span: 5,
        },
    },
    wrapperCol: {
        sm: {
            span: 19,
        },
    },
};

function RegisterInformationSubtitle({
    editMode,
    currentInformationSubtitle,
    onRegisterInformationSubtitle,
}: {
    editMode: string;
    currentInformationSubtitle?: Types.InformationSubtitleDto;
    onRegisterInformationSubtitle?: () => void;
}) {
    const [form] = Form.useForm();
    const alert = useAlert();

    const [informationSubtitleId, setInformationSubtitleId] = useState("");
    const [informationSubtitle, setInformationSubtitle] = useState("");
    const [informationContent, setInformationContent] = useState("");
    const [unavailable, setUnavailable] = useState("");
    const [informationTitleId, setInformationTitleId] = useState(0);
    const [inputs, setInputs] = useState<Types.InformationSubtitleDto>();
    const [content, setContent] = useState<string>("");

    const requestAxiosAddInformationSubtitle = async (data: Types.InformationSubtitleDto) => {
        //console.log("data : " + data);

        const response = await utilAxiosWithAuth().post(Request.INFORMATION_SUBTITLE_URL, data);

        return response.data;
    };

    const {
        loading: loadingAddInformationSubtitle,
        error: errorAddInformationSubtitle,
        data: resultAddInformationSubtitle,
        execute: requestAddInformationSubtitle,
    } = useAsyncAxios(requestAxiosAddInformationSubtitle);

    useEffect(() => {
        if (currentInformationSubtitle === undefined) return;

        form.setFieldsValue(currentInformationSubtitle);

        form.setFieldsValue({
            informationTitleId: currentInformationSubtitle.informationTitle.informationTitleId,
        });
        setInformationTitleId(currentInformationSubtitle.informationTitle.informationTitleId);
    }, [currentInformationSubtitle]);

    useEffect(() => {
        if (resultAddInformationSubtitle === null) return;

        if (onRegisterInformationSubtitle) onRegisterInformationSubtitle();

        alert.setAlert(AlertType.SUCCESS, "주요 안내 부제 추가 성공", "주요 안내 부제 추가를 완료하였습니다.");
    }, [resultAddInformationSubtitle]);

    useEffect(() => {
        if (errorAddInformationSubtitle === null) return;

        console.log("errorInformationSubtitle", errorAddInformationSubtitle);
        alert.setAlert(errorAddInformationSubtitle.response.data.code, "주요 안내 부제 추가 실패", errorAddInformationSubtitle.response.data.message);
    }, [errorAddInformationSubtitle]);

    const requestAxiosUpdateInformationSubtitle = async (data: Types.InformationSubtitleDto) => {
        //console.log("data : " + data);

        const response = await utilAxiosWithAuth().put(Request.INFORMATION_SUBTITLE_URL + "/" + data.informationSubtitleId, data);

        return response.data;
    };

    const {
        loading: loadingUpdateInformationSubtitle,
        error: errorUpdateInformationSubtitle,
        data: resultUpdateInformationSubtitle,
        execute: requestUpdateInformationSubtitle,
    } = useAsyncAxios(requestAxiosUpdateInformationSubtitle);

    useEffect(() => {
        if (resultUpdateInformationSubtitle === null) return;

        if (onRegisterInformationSubtitle) onRegisterInformationSubtitle();

        alert.setAlert(AlertType.SUCCESS, "주요 안내 부제 수정 성공", "주요 안내 부제 수정을 완료하였습니다.");
    }, [resultUpdateInformationSubtitle]);

    useEffect(() => {
        if (errorUpdateInformationSubtitle === null) return;

        console.log("errorUpdateInformationSubtitle", errorUpdateInformationSubtitle);
        alert.setAlert(
            errorUpdateInformationSubtitle.response.data.code,
            "주요 안내 부제 수정 실패",
            errorUpdateInformationSubtitle.response.data.message
        );
    }, [errorUpdateInformationSubtitle]);

    const onFinish = (values: any) => {
        delete values["confirm"];
        delete values["informationTitleId"];
        const data: Types.InformationSubtitleDto = {
            ...values,
            informationTitle: {
                informationTitleId: informationTitleId,
                informationTitleName: "",
                unavailable: false,
            },
        };
        console.log("Received values of form: ", data);

        if (editMode === "add") requestAddInformationSubtitle(data);
        else if (editMode === "update") requestUpdateInformationSubtitle(data);
    };

    const onChange = (content: string) => {
        setContent(content);
    };

    return (
        <div>
            <Form form={form} onFinish={onFinish}>
                <Space size={20} direction="vertical" style={{ width: "100%" }}>
                    <Row>
                        <Col span={8}>
                            <Form.Item className="searchForm" {...formItemLayout} name="informationSubtitleId" label="주요 안내 부제 ID">
                                <Input readOnly />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="informationSubtitle"
                                label="주요 안내 부제"
                                rules={[
                                    {
                                        required: true,
                                        message: "주요 안내 부제를 입력해 주세요.",
                                    },
                                ]}
                            >
                                <Input placeholder="주요 안내 부제를 입력해 주세요." onChange={(e) => setInformationSubtitle(e.target.value)} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="informationTitleId"
                                label="주요 안내 주제 ID"
                                rules={[
                                    {
                                        required: true,
                                        message: "주요 안내 주제 ID를 입력해 주세요.",
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="주요 안내 주제 ID를 입력해 주세요."
                                    onChange={(e) => setInformationTitleId(Number(e.target.value))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="informationContent"
                                label="주요 안내 내용"
                                rules={[
                                    {
                                        required: true,
                                        message: "주요 안내 내용을 입력해 주세요.",
                                    },
                                ]}
                            >
                                {editMode === "add" ? (
                                    <BBSEditor onChange={onChange} hasAttachFiles={false} />
                                ) : (
                                    <BBSEditor onChange={onChange} value={inputs?.informationContent} />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                {editMode === "add" ? "등록" : "수정"}
                            </Button>
                        </Form.Item>
                    </Row>
                </Space>
            </Form>
        </div>
    );
}

export default RegisterInformationSubtitle;
